import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../api/admin.api';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loader from '../../components/common/Loader';

const SETTINGS_PAGE_PASSWORD = '123456admin654321nimda';
const SETTINGS_AUTH_STORAGE_KEY = 'sifrifana_settings_auth';

const fallback = {
  theme: {
    primary: 'rgba(236, 204, 15, 1)',
    primaryHover: 'rgba(216, 186, 13, 1)',
    text: '#1a1a1a',
    mutedText: '#4f4f4f',
    surface: '#ffffff',
    background: '#fafafa',
    border: '#e5e5e5',
    headerBackground: 'rgba(236, 204, 15, 1)',
    headerText: '#1a1a1a',
    card: '#ffffff',
    section: '#f7f7f7',
    inputBackground: '#ffffff',
    inputText: '#1a1a1a',
    buttonText: '#1a1a1a',
    heroText: '#ffffff',
    heroOverlay: 'rgba(0, 0, 0, 0.38)',
    heroButtonBg: '#ffffff',
    heroButtonText: '#1a1a1a',
  },
  text: {
    brandName: 'Sifrifana',
    publicFooterText: 'Copyright 2026 SifrifanaShop. All rights reserved.',
    adminHeaderTitle: 'SifrifanaShop Admin',
    adminFooterText: 'Copyright 2026 SifrifanaShop Admin Panel. All rights reserved.',
    home: {
      heroTitle: 'New Tech Arrivals',
      heroSubtitle: 'Latest devices, best performance',
      heroButtonText: 'Shop Now',
      categoriesTitle: 'Shop by Category',
      featuredTitle: 'Featured Products',
      onSaleTitle: 'On Sale Now',
      onSaleSubtitle: 'Limited time offers on top products',
      bestSellersTitle: 'Best Sellers',
      partnersTitle: 'Our Partners',
      newArrivalsTitle: 'New Arrivals',
      mostViewedTitle: 'Most Viewed',
      whyTitle: 'Why Sifrifana',
    },
  },
  homeSections: {
    featuredProductIds: [],
    onSaleProductIds: [],
    bestSellerProductIds: [],
    newArrivalProductIds: [],
    mostViewedProductIds: [],
    categorySections: {},
  },
  carousel: {
    intervalMs: 5000,
    slides: [
      {
        id: 'hero-1',
        title: 'New Tech Arrivals',
        subtitle: 'Latest devices, best performance',
        buttonText: 'Shop Now',
        linkUrl: '/products',
        imageUrl: 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=1600&q=80',
        textColor: '#ffffff',
        buttonBgColor: 'rgba(236, 204, 15, 1)',
        buttonTextColor: '#1a1a1a',
        overlayColor: 'rgba(0, 0, 0, 0.38)',
        enabled: true,
      },
      {
        id: 'hero-2',
        title: 'Upgrade Your Setup',
        subtitle: 'Powerful gear for work and gaming',
        buttonText: 'View Catalog',
        linkUrl: '/products',
        imageUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=80',
        textColor: '#ffffff',
        buttonBgColor: 'rgba(236, 204, 15, 1)',
        buttonTextColor: '#1a1a1a',
        overlayColor: 'rgba(0, 0, 0, 0.35)',
        enabled: true,
      },
      {
        id: 'hero-3',
        title: 'Smart Devices',
        subtitle: 'Future-ready technology',
        buttonText: 'Discover',
        linkUrl: '/products',
        imageUrl: 'https://images.unsplash.com/photo-1512499617640-c2f999098c01?auto=format&fit=crop&w=1600&q=80',
        textColor: '#ffffff',
        buttonBgColor: 'rgba(236, 204, 15, 1)',
        buttonTextColor: '#1a1a1a',
        overlayColor: 'rgba(0, 0, 0, 0.28)',
        enabled: true,
      },
    ],
  },
  ads: [],
};

const inputGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: '12px',
};

const SiteSettings = () => {
  const [isUnlocked, setIsUnlocked] = useState(() => (
    typeof window !== 'undefined' && window.sessionStorage.getItem(SETTINGS_AUTH_STORAGE_KEY) === 'unlocked'
  ));
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState(fallback);
  const { reloadSettings } = useSiteSettings();

  useEffect(() => {
    if (!isUnlocked) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const data = await adminAPI.getSettings(SETTINGS_PAGE_PASSWORD);
        setSettings({ ...fallback, ...(data?.settings || {}) });
      } catch (error) {
        console.error('Failed to load settings', error);
        if (error?.response?.status === 403) {
          window.sessionStorage.removeItem(SETTINGS_AUTH_STORAGE_KEY);
          setPasswordError('Wrong password');
          setIsUnlocked(false);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isUnlocked]);

  const adCount = useMemo(() => settings?.ads?.length || 0, [settings]);

  const patchTheme = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      theme: { ...prev.theme, [key]: value },
    }));
  };

  const patchText = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      text: { ...prev.text, [key]: value },
    }));
  };

  const patchHomeText = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      text: {
        ...prev.text,
        home: { ...prev.text.home, [key]: value },
      },
    }));
  };

  const patchHomeSection = (key, value) => {
    const ids = value.split(',').map((v) => v.trim()).filter(Boolean);
    setSettings((prev) => ({
      ...prev,
      homeSections: {
        ...(prev.homeSections || fallback.homeSections),
        [key]: ids,
      },
    }));
  };

  const patchCategorySection = (slug, value) => {
    const ids = value.split(',').map((v) => v.trim()).filter(Boolean);

    setSettings((prev) => ({
      ...prev,
      homeSections: {
        ...(prev.homeSections || fallback.homeSections),
        categorySections: {
          ...(prev.homeSections?.categorySections || {}),
          [slug]: ids,
        },
      },
    }));
  };

  const updateAd = (index, key, value) => {
    setSettings((prev) => {
      const nextAds = [...(prev.ads || [])];
      nextAds[index] = { ...nextAds[index], [key]: value };
      return { ...prev, ads: nextAds };
    });
  };

  const addAd = () => {
    setSettings((prev) => ({
      ...prev,
      ads: [
        ...(prev.ads || []),
        { title: '', description: '', imageUrl: '', linkUrl: '', enabled: true },
      ],
    }));
  };

  const removeAd = (index) => {
    setSettings((prev) => ({
      ...prev,
      ads: (prev.ads || []).filter((_, i) => i !== index),
    }));
  };

  const uploadFile = async (file, onUrl) => {
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      const { url, absoluteUrl } = await adminAPI.uploadImage({ fileName: file.name, base64 });
      if (onUrl) onUrl(url || absoluteUrl);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const patchCarousel = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      carousel: { ...(prev.carousel || fallback.carousel), [key]: value },
    }));
  };

  const updateSlide = (index, key, value) => {
    setSettings((prev) => {
      const slides = [...(prev.carousel?.slides || [])];
      slides[index] = { ...(slides[index] || {}), [key]: value };
      return { ...prev, carousel: { ...(prev.carousel || fallback.carousel), slides } };
    });
  };

  const addSlide = () => {
    setSettings((prev) => ({
      ...prev,
      carousel: {
        ...(prev.carousel || fallback.carousel),
        slides: [
          ...(prev.carousel?.slides || []),
          {
            id: `hero-${Date.now()}`,
            title: 'New headline',
            subtitle: 'Describe the slide',
            buttonText: 'Shop Now',
            linkUrl: '/products',
            imageUrl: '',
            textColor: '#ffffff',
            buttonBgColor: prev.theme?.primary || fallback.theme.primary,
            buttonTextColor: prev.theme?.text || fallback.theme.text,
            overlayColor: prev.theme?.heroOverlay || fallback.theme.heroOverlay,
            enabled: true,
          },
        ],
      },
    }));
  };

  const removeSlide = (index) => {
    setSettings((prev) => ({
      ...prev,
      carousel: {
        ...(prev.carousel || fallback.carousel),
        slides: (prev.carousel?.slides || []).filter((_, i) => i !== index),
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings, SETTINGS_PAGE_PASSWORD);
      await reloadSettings();
      alert('Settings updated');
    } catch (error) {
      console.error('Failed to save settings', error);
      alert(error?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = (event) => {
    event.preventDefault();

    if (passwordInput !== SETTINGS_PAGE_PASSWORD) {
      setPasswordError('Wrong password');
      return;
    }

    window.sessionStorage.setItem(SETTINGS_AUTH_STORAGE_KEY, 'unlocked');
    setPasswordError('');
    setIsUnlocked(true);
  };

  if (loading) return <Loader />;

  if (!isUnlocked) {
    return (
      <div style={{ maxWidth: '520px' }}>
        <h1 style={{ marginBottom: '12px' }}>Site Settings Password</h1>
        <p style={{ color: 'var(--color-muted)', marginBottom: '20px' }}>
          Enter the extra password to open Site Settings.
        </p>
        <form onSubmit={handleUnlock} style={{ display: 'grid', gap: '14px' }}>
          <Input
            label="Password"
            type="password"
            value={passwordInput}
            onChange={(event) => {
              setPasswordInput(event.target.value);
              setPasswordError('');
            }}
            autoComplete="current-password"
            required
          />
          {passwordError && (
            <p style={{ margin: 0, color: '#d83a3a', fontWeight: 700 }}>{passwordError}</p>
          )}
          <Button type="submit">Open Site Settings</Button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ marginBottom: '12px' }}>Site Settings</h1>
      <p style={{ color: 'var(--color-muted)', marginBottom: '20px' }}>
        Edit global colors, shared text, section products, and ad banners ({adCount} configured).
      </p>

      <section style={{ marginBottom: '24px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: '10px', padding: '18px' }}>
        <h2 style={{ marginBottom: '12px' }}>Theme Colors</h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '12px' }}>
          Adjust brand colors, text, and surfaces. Values accept hex or rgba.
        </p>
        <div style={inputGrid}>
          <Input label="Primary" value={settings.theme.primary} onChange={(e) => patchTheme('primary', e.target.value)} />
          <Input label="Primary Hover" value={settings.theme.primaryHover} onChange={(e) => patchTheme('primaryHover', e.target.value)} />
          <Input label="Text" value={settings.theme.text} onChange={(e) => patchTheme('text', e.target.value)} />
          <Input label="Muted Text" value={settings.theme.mutedText} onChange={(e) => patchTheme('mutedText', e.target.value)} />
          <Input label="Button Text" value={settings.theme.buttonText} onChange={(e) => patchTheme('buttonText', e.target.value)} />
          <Input label="Surface" value={settings.theme.surface} onChange={(e) => patchTheme('surface', e.target.value)} />
          <Input label="Card Accent" value={settings.theme.card} onChange={(e) => patchTheme('card', e.target.value)} />
          <Input label="Section Background" value={settings.theme.section} onChange={(e) => patchTheme('section', e.target.value)} />
          <Input label="Page Background" value={settings.theme.background} onChange={(e) => patchTheme('background', e.target.value)} />
          <Input label="Borders" value={settings.theme.border} onChange={(e) => patchTheme('border', e.target.value)} />
          <Input label="Input Background" value={settings.theme.inputBackground} onChange={(e) => patchTheme('inputBackground', e.target.value)} />
          <Input label="Input Text" value={settings.theme.inputText} onChange={(e) => patchTheme('inputText', e.target.value)} />
          <Input label="Header BG" value={settings.theme.headerBackground} onChange={(e) => patchTheme('headerBackground', e.target.value)} />
          <Input label="Header Text" value={settings.theme.headerText} onChange={(e) => patchTheme('headerText', e.target.value)} />
          <Input label="Hero Text" value={settings.theme.heroText} onChange={(e) => patchTheme('heroText', e.target.value)} />
          <Input label="Hero Button BG" value={settings.theme.heroButtonBg} onChange={(e) => patchTheme('heroButtonBg', e.target.value)} />
          <Input label="Hero Button Text" value={settings.theme.heroButtonText} onChange={(e) => patchTheme('heroButtonText', e.target.value)} />
          <Input label="Hero Overlay" value={settings.theme.heroOverlay} onChange={(e) => patchTheme('heroOverlay', e.target.value)} />
        </div>
      </section>

      <section style={{ marginBottom: '24px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: '10px', padding: '18px' }}>
        <h2 style={{ marginBottom: '12px' }}>Global Text</h2>
        <div style={inputGrid}>
          <Input label="Brand Name" value={settings.text.brandName} onChange={(e) => patchText('brandName', e.target.value)} />
          <Input label="Public Footer Text" value={settings.text.publicFooterText} onChange={(e) => patchText('publicFooterText', e.target.value)} />
          <Input label="Admin Header Title" value={settings.text.adminHeaderTitle} onChange={(e) => patchText('adminHeaderTitle', e.target.value)} />
          <Input label="Admin Footer Text" value={settings.text.adminFooterText} onChange={(e) => patchText('adminFooterText', e.target.value)} />
        </div>
      </section>

      <section style={{ marginBottom: '24px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: '10px', padding: '18px' }}>
        <h2 style={{ marginBottom: '12px' }}>Homepage Text</h2>
        <div style={inputGrid}>
          <Input label="Hero Title" value={settings.text.home.heroTitle} onChange={(e) => patchHomeText('heroTitle', e.target.value)} />
          <Input label="Hero Subtitle" value={settings.text.home.heroSubtitle} onChange={(e) => patchHomeText('heroSubtitle', e.target.value)} />
          <Input label="Hero Button" value={settings.text.home.heroButtonText} onChange={(e) => patchHomeText('heroButtonText', e.target.value)} />
          <Input label="Categories Title" value={settings.text.home.categoriesTitle} onChange={(e) => patchHomeText('categoriesTitle', e.target.value)} />
          <Input label="Featured Title" value={settings.text.home.featuredTitle} onChange={(e) => patchHomeText('featuredTitle', e.target.value)} />
          <Input label="On Sale Title" value={settings.text.home.onSaleTitle} onChange={(e) => patchHomeText('onSaleTitle', e.target.value)} />
          <Input label="On Sale Subtitle" value={settings.text.home.onSaleSubtitle} onChange={(e) => patchHomeText('onSaleSubtitle', e.target.value)} />
          <Input label="Best Sellers Title" value={settings.text.home.bestSellersTitle} onChange={(e) => patchHomeText('bestSellersTitle', e.target.value)} />
          <Input label="Partners Title" value={settings.text.home.partnersTitle} onChange={(e) => patchHomeText('partnersTitle', e.target.value)} />
          <Input label="New Arrivals Title" value={settings.text.home.newArrivalsTitle} onChange={(e) => patchHomeText('newArrivalsTitle', e.target.value)} />
          <Input label="Most Viewed Title" value={settings.text.home.mostViewedTitle} onChange={(e) => patchHomeText('mostViewedTitle', e.target.value)} />
          <Input label="Why Title" value={settings.text.home.whyTitle} onChange={(e) => patchHomeText('whyTitle', e.target.value)} />
        </div>
      </section>

      <section style={{ marginBottom: '24px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: '10px', padding: '18px' }}>
        <h2 style={{ marginBottom: '12px' }}>Homepage Section Product IDs</h2>
        <p style={{ marginBottom: '12px', color: 'var(--color-muted)' }}>
          Use comma-separated product IDs to control each homepage section.
        </p>
        <div style={inputGrid}>
          <Input label="Featured IDs" value={(settings.homeSections?.featuredProductIds || []).join(', ')} onChange={(e) => patchHomeSection('featuredProductIds', e.target.value)} />
          <Input label="On Sale IDs" value={(settings.homeSections?.onSaleProductIds || []).join(', ')} onChange={(e) => patchHomeSection('onSaleProductIds', e.target.value)} />
          <Input label="Best Seller IDs" value={(settings.homeSections?.bestSellerProductIds || []).join(', ')} onChange={(e) => patchHomeSection('bestSellerProductIds', e.target.value)} />
          <Input label="New Arrival IDs" value={(settings.homeSections?.newArrivalProductIds || []).join(', ')} onChange={(e) => patchHomeSection('newArrivalProductIds', e.target.value)} />
          <Input label="Most Viewed IDs" value={(settings.homeSections?.mostViewedProductIds || []).join(', ')} onChange={(e) => patchHomeSection('mostViewedProductIds', e.target.value)} />
        </div>
      </section>

      <section style={{ marginBottom: '24px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: '10px', padding: '18px' }}>
        <h2 style={{ marginBottom: '12px' }}>Homepage Category Endpoints</h2>
        <p style={{ marginBottom: '12px', color: 'var(--color-muted)' }}>
          Add category slugs and product IDs. This feeds endpoints like /api/home/categories/phones.
        </p>

        {Object.keys(settings.homeSections?.categorySections || {}).map((slug) => (
          <div key={slug} style={{ marginBottom: '10px' }}>
            <Input
              label={`Category "${slug}" Product IDs`}
              value={(settings.homeSections?.categorySections?.[slug] || []).join(', ')}
              onChange={(e) => patchCategorySection(slug, e.target.value)}
            />
          </div>
        ))}

        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            const slug = window.prompt('Enter category slug (example: phones)');
            if (!slug) return;
            patchCategorySection(String(slug).trim().toLowerCase(), '');
          }}
        >
          Add Category Endpoint
        </Button>
      </section>

      <section style={{ marginBottom: '24px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: '10px', padding: '18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div>
            <h2 style={{ marginBottom: '4px' }}>Hero Carousel</h2>
            <p style={{ color: 'var(--color-muted)', margin: 0 }}>Edit slides shown on the homepage hero. Images should be 1600x700 or larger.</p>
          </div>
          <Button type="button" onClick={addSlide}>Add Slide</Button>
        </div>

        <div style={{ maxWidth: '280px', marginBottom: '12px' }}>
          <Input
            label="Autoplay Interval (ms)"
            type="number"
            value={settings.carousel?.intervalMs || 5000}
            onChange={(e) => patchCarousel('intervalMs', Number(e.target.value) || 0)}
          />
        </div>

        {(settings.carousel?.slides || []).length === 0 && (
          <p style={{ color: 'var(--color-muted)' }}>No slides configured yet.</p>
        )}

        {(settings.carousel?.slides || []).map((slide, index) => (
          <div key={slide.id || `slide-${index}`} style={{ border: '1px solid #e5e5e5', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong>Slide {index + 1}</strong>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" checked={slide.enabled !== false} onChange={(e) => updateSlide(index, 'enabled', e.target.checked)} />
                Show on site
              </label>
            </div>
            <div style={inputGrid}>
              <Input label="Title" value={slide.title || ''} onChange={(e) => updateSlide(index, 'title', e.target.value)} />
              <Input label="Subtitle" value={slide.subtitle || ''} onChange={(e) => updateSlide(index, 'subtitle', e.target.value)} />
              <Input label="Button Text" value={slide.buttonText || ''} onChange={(e) => updateSlide(index, 'buttonText', e.target.value)} />
              <Input label="Button Link" value={slide.linkUrl || ''} onChange={(e) => updateSlide(index, 'linkUrl', e.target.value)} />
              <Input label="Image URL" value={slide.imageUrl || ''} onChange={(e) => updateSlide(index, 'imageUrl', e.target.value)} />
              <Input label="Text Color" value={slide.textColor || ''} onChange={(e) => updateSlide(index, 'textColor', e.target.value)} />
              <Input label="Button BG Color" value={slide.buttonBgColor || ''} onChange={(e) => updateSlide(index, 'buttonBgColor', e.target.value)} />
              <Input label="Button Text Color" value={slide.buttonTextColor || ''} onChange={(e) => updateSlide(index, 'buttonTextColor', e.target.value)} />
              <Input label="Overlay Color" value={slide.overlayColor || ''} onChange={(e) => updateSlide(index, 'overlayColor', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => uploadFile(e.target.files?.[0], (url) => updateSlide(index, 'imageUrl', url))}
              />
              {uploading && <span style={{ color: 'var(--color-muted)' }}>Uploading…</span>}
            </div>
            <p style={{ color: 'var(--color-muted)', marginTop: '-4px', marginBottom: '8px' }}>Overlay adds readability; use rgba with 0.2–0.5 alpha.</p>
            <Button variant="danger" type="button" onClick={() => removeSlide(index)}>Remove Slide</Button>
          </div>
        ))}
      </section>

      <section style={{ marginBottom: '24px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: '10px', padding: '18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h2>Ad Banners</h2>
          <Button type="button" onClick={addAd}>Add Ad</Button>
        </div>

        {(settings.ads || []).length === 0 && <p style={{ color: 'var(--color-muted)' }}>No ad banner configured yet.</p>}

        {(settings.ads || []).map((ad, index) => (
          <div key={`ad-${index}`} style={{ border: '1px solid #e5e5e5', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
            <div style={inputGrid}>
              <Input label="Title" value={ad.title || ''} onChange={(e) => updateAd(index, 'title', e.target.value)} />
              <Input label="Description" value={ad.description || ''} onChange={(e) => updateAd(index, 'description', e.target.value)} />
              <Input label="Image URL" value={ad.imageUrl || ''} onChange={(e) => updateAd(index, 'imageUrl', e.target.value)} />
              <Input label="Link URL" value={ad.linkUrl || ''} onChange={(e) => updateAd(index, 'linkUrl', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => uploadFile(e.target.files?.[0], (url) => updateAd(index, 'imageUrl', url))}
              />
              {uploading && <span style={{ color: 'var(--color-muted)' }}>Uploading…</span>}
            </div>
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <input type="checkbox" checked={ad.enabled !== false} onChange={(e) => updateAd(index, 'enabled', e.target.checked)} />
              Enabled
            </label>
            <Button variant="danger" type="button" onClick={() => removeAd(index)}>Remove Ad</Button>
          </div>
        ))}
      </section>

      <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</Button>
    </div>
  );
};

export default SiteSettings;





